import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { merge, fromEvent, interval, combineLatest, Observable, of, EMPTY, from } from 'rxjs';
import { map, startWith, switchMap, distinctUntilChanged, shareReplay } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class ConnectionService {

	private connectionMonitor: Observable<boolean>;
	private checkUrl = () => 'https://client3.google.com/generate_204';

	constructor(@Inject(PLATFORM_ID) private platform: object) {
		if (isPlatformBrowser(platform)) {
			const browserStatus$ = merge(
				fromEvent(window, 'online'),
				fromEvent(window, 'offline')
			).pipe(
				startWith(null),
				map(() => navigator.onLine),
				distinctUntilChanged(),
				shareReplay({ bufferSize: 1, refCount: true })
			);

			const serverStatus$ = browserStatus$.pipe(
				switchMap(isOnline => {
					if (!isOnline) return of(false);

					return interval(10000).pipe(
						startWith(0),
						switchMap(() => {
							// Nochmals prüfen — Navigator-Status kann sich zwischen Intervallen ändern
							if (!navigator.onLine) return of(false);
							return from(
								fetch(this.checkUrl(), {
									method: 'GET',
									cache: 'no-store',
									mode: 'no-cors',
									redirect: 'follow'
								})
									.then(resp => {
										return !resp.status || resp.status === 204;
									})
									.catch(() => {
										return false;
									})
							);
						}),
						distinctUntilChanged()
					);
				}),
				shareReplay({ bufferSize: 1, refCount: true })
			);

			this.connectionMonitor = combineLatest([browserStatus$, serverStatus$]).pipe(
				map(([browserOnline, serverReachable]) => browserOnline && serverReachable),
				distinctUntilChanged(),
				shareReplay({ bufferSize: 1, refCount: true })
			);
		} else {
			this.connectionMonitor = EMPTY;
		}
	}

	monitor(): Observable<boolean> {
		return this.connectionMonitor;
	}
}
